import React, { useEffect, useRef, useState } from "react";

// Title search (debounced) + barcode/UPC scan input (Enter). The scan box is styled as the PRIMARY
// input — at a counter the scanner is the main way product reaches the cart, and on a phone it takes
// the full first row (see register.css) because a two-up split leaves neither field usable.
export default function SearchScanBar({ onSearch, onScan, onHome, onCamera, view, clearSignal = 0 }) {
  const [text, setText] = useState("");
  const scanRef = useRef(null);
  const skipSearch = useRef(false);

  useEffect(() => {
    // A clear driven by opening a category must NOT re-run the search (an onSearch("") would bounce
    // the view straight back to the category grid). Swallow exactly that one debounce.
    if (skipSearch.current) {
      skipSearch.current = false;
      return;
    }
    const id = setTimeout(() => onSearch(text.trim()), 300);
    return () => clearTimeout(id);
  }, [text, onSearch]);

  // POS clears the product-search box when a category is opened. The parent bumps `clearSignal` on
  // every category open / back-to-grid; clear the field here without triggering another search.
  useEffect(() => {
    if (clearSignal === 0) return; // initial mount — nothing typed
    setText((prev) => {
      if (prev) skipSearch.current = true; // only swallow a search if there was text to clear
      return "";
    });
    if (scanRef.current) scanRef.current.value = "";
  }, [clearSignal]);
  // NOTE: intentionally NOT auto-focusing the scan box on view change — it stole focus from the
  // search box mid-typing. The scan box is focused only when the user clicks into it.

  return (
    <div className="qv-search">
      <label className="qv-field qv-field--scan">
        <span className="qv-ico" aria-hidden="true">▮▯▮</span>
        <input
          ref={scanRef}
          placeholder="Scan barcode…"
          aria-label="Scan barcode"
          inputMode="numeric"
          onFocus={() => setText("")} // POS: moving to the scan field clears the search text
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const code = e.currentTarget.value.trim();
              if (code) {
                onScan(code);
                e.currentTarget.value = "";
              }
            }
          }}
        />
        {/* Camera scan lives INSIDE the scan field — same home as the wedge scanner it stands in
            for. A <button> is interactive, so clicking it does NOT trigger the label's
            focus-the-input behavior (the HTML spec's interactive-element exception). */}
        {onCamera && (
          <button
            type="button"
            className="qv-btn qv-cam-open"
            onClick={onCamera}
            title="Scan with the camera"
            aria-label="Scan with the camera"
          >
            {/* Inline SVG, not a text glyph: every crosshair-ish character (⌖ ◎ ⊕) reads as a map
                target, and there is no unmistakable monochrome camera in the safe glyph range. The
                SVG strokes with currentColor, so it themes exactly like the glyphs it sits beside. */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 7h3.2L9.4 4.5h5.2L16.8 7H20a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 20 19H4a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 4 7Z" />
              <circle cx="12" cy="12.5" r="3.4" />
            </svg>
          </button>
        )}
      </label>

      <label className="qv-field qv-field--name">
        <span className="qv-ico" aria-hidden="true">⌕</span>
        <input
          placeholder="Search products by name, UPC, or code"
          aria-label="Search products by name, UPC, or code"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => { if (scanRef.current) scanRef.current.value = ""; }} // POS: moving to search clears the scan field
        />
      </label>

      <button
        className="qv-btn qv-btn--ghost"
        onClick={() => { setText(""); onHome(); }}
        title="Back to categories"
      >
        <span aria-hidden="true">◧</span>
        <span className="qv-top-label">Categories</span>
      </button>
    </div>
  );
}
